import { useState, useEffect } from 'react';
import { CalcSettings, DEFAULT_SETTINGS, LocationData, TodayPrayerTimes, NextPrayerInfo } from '../types';

interface AdhanSettings {
  enabled: boolean;
  soundId: string;
}

interface NotificationSettings {
  enabled: boolean;
  Fajr: boolean;
  Sunrise: boolean;
  Dhuhr: boolean;
  Asr: boolean;
  Maghrib: boolean;
  Isha: boolean;
}

export type IshaFixedMode = 'default' | 'afterMaghrib' | 'fixedTime' | 'minutesFromIsha';
export type FajrFixedMode = 'default' | 'beforeSunrise' | 'fixedTime' | 'minutesFromFajr';

export interface IshaFixedSettings {
  mode: IshaFixedMode;
  minutesAfterMaghrib: number;
  minutesFromRealIsha?: number;
  fixedTime: string;
}

export interface FajrFixedSettings {
  mode: FajrFixedMode;
  minutesBeforeSunrise: number;
  minutesFromRealFajr?: number;
  fixedTime: string;
}

interface GlobalStorageData {
  calcMethod: string;
  madhab: string;
  calcSettings: CalcSettings;
  manualLocation: LocationData;
  useGPS: boolean;
  adhanEnabled: boolean;
  adhanSettings: AdhanSettings;
  notificationSettings: NotificationSettings;
  persistentNotifications: boolean;
  ishaFixedSettings: IshaFixedSettings;
  fajrFixedSettings: FajrFixedSettings;
  hijriAdjustment: number;
  todayPrayerTimes: TodayPrayerTimes | null;
  nextPrayer: NextPrayerInfo | null;
}

const STORAGE_KEYS = {
  CALC_METHOD: 'calcMethod',
  MADHAB: 'madhab',
  CALC_SETTINGS: 'calcSettings',
  LOCATION: 'location',
  USE_GPS: 'useGPS',
  ADHAN_ENABLED: 'adhanEnabled',
  ADHAN_SETTINGS: 'adhanSettings',
  NOTIFICATION_SETTINGS: 'notificationSettings',
  PERSISTENT_NOTIFICATIONS: 'persistentNotifications',
  ISHA_FIXED_SETTINGS: 'ishaFixedSettings',
  FAJR_FIXED_SETTINGS: 'fajrFixedSettings',
  HIJRI_ADJUSTMENT: 'hijriAdjustment',
  TODAY_PRAYER_TIMES: 'todayPrayerTimes',
  NEXT_PRAYER: 'nextPrayer',
} as const;

export const useGlobalStorage = () => {
  const [data, setData] = useState<GlobalStorageData>({
    calcMethod: 'MuslimWorldLeague',
    madhab: 'Shafi',
    calcSettings: DEFAULT_SETTINGS,
    manualLocation: { city: 'Helsinki', latitude: 60.1695, longitude: 24.9354 },
    useGPS: false,
    adhanEnabled: false,
    adhanSettings: { enabled: false, soundId: 'azan' },
    notificationSettings: { enabled: false, Fajr: true, Sunrise: true, Dhuhr: true, Asr: true, Maghrib: true, Isha: true },
    persistentNotifications: true,
    ishaFixedSettings: { mode: 'default', minutesAfterMaghrib: 90, fixedTime: '20:00' },
    fajrFixedSettings: { mode: 'default', minutesBeforeSunrise: 90, fixedTime: '05:00' },
    hijriAdjustment: 0,
    todayPrayerTimes: { fajr: '05:30', sunrise: '07:15', dhuhr: '12:45', asr: '14:30', maghrib: '16:45', isha: '18:30' },
    nextPrayer: { name: 'Fajr', time: '05:30' },
  });

  useEffect(() => {
    const loadData = () => {
      const defaults = {
        calcMethod: 'MuslimWorldLeague',
        madhab: 'Shafi',
        calcSettings: DEFAULT_SETTINGS,
        manualLocation: { city: 'Helsinki', latitude: 60.1695, longitude: 24.9354 },
        useGPS: false,
        adhanEnabled: false,
        adhanSettings: { enabled: false, soundId: 'azan' },
        notificationSettings: { enabled: false, Fajr: true, Sunrise: true, Dhuhr: true, Asr: true, Maghrib: true, Isha: true },
        persistentNotifications: true,
        ishaFixedSettings: { mode: 'default', minutesAfterMaghrib: 90, fixedTime: '20:00' } as IshaFixedSettings,
        fajrFixedSettings: { mode: 'default', minutesBeforeSunrise: 90, fixedTime: '05:00' } as FajrFixedSettings,
        hijriAdjustment: 0,
        todayPrayerTimes: { fajr: '05:30', sunrise: '07:15', dhuhr: '12:45', asr: '14:30', maghrib: '16:45', isha: '18:30' },
        nextPrayer: { name: 'Fajr', time: '05:30' },
      };

      const calcMethod = localStorage.getItem(STORAGE_KEYS.CALC_METHOD) || defaults.calcMethod;
      const madhab = localStorage.getItem(STORAGE_KEYS.MADHAB) || defaults.madhab;
      const calcSettings = JSON.parse(localStorage.getItem(STORAGE_KEYS.CALC_SETTINGS) || JSON.stringify(defaults.calcSettings));
      const manualLocation = JSON.parse(localStorage.getItem(STORAGE_KEYS.LOCATION) || JSON.stringify(defaults.manualLocation));
      const useGPS = localStorage.getItem(STORAGE_KEYS.USE_GPS) === 'true';
      const adhanEnabled = localStorage.getItem(STORAGE_KEYS.ADHAN_ENABLED) === 'true';
      const adhanSettings = JSON.parse(localStorage.getItem(STORAGE_KEYS.ADHAN_SETTINGS) || JSON.stringify(defaults.adhanSettings));
      const notificationSettings = JSON.parse(localStorage.getItem(STORAGE_KEYS.NOTIFICATION_SETTINGS) || JSON.stringify(defaults.notificationSettings));
      const persistentNotifications = localStorage.getItem(STORAGE_KEYS.PERSISTENT_NOTIFICATIONS) !== 'false';
      const ishaFixedSettings = JSON.parse(localStorage.getItem(STORAGE_KEYS.ISHA_FIXED_SETTINGS) || JSON.stringify(defaults.ishaFixedSettings));
      const fajrFixedSettings = JSON.parse(localStorage.getItem(STORAGE_KEYS.FAJR_FIXED_SETTINGS) || JSON.stringify(defaults.fajrFixedSettings));
      const hijriAdjustment = Number(localStorage.getItem(STORAGE_KEYS.HIJRI_ADJUSTMENT) || '0');
      const todayPrayerTimes = JSON.parse(localStorage.getItem(STORAGE_KEYS.TODAY_PRAYER_TIMES) || JSON.stringify(defaults.todayPrayerTimes));
      const nextPrayer = JSON.parse(localStorage.getItem(STORAGE_KEYS.NEXT_PRAYER) || JSON.stringify(defaults.nextPrayer));

      setData({
        calcMethod,
        madhab,
        calcSettings,
        manualLocation,
        useGPS,
        adhanEnabled,
        adhanSettings,
        notificationSettings,
        persistentNotifications,
        ishaFixedSettings,
        fajrFixedSettings,
        hijriAdjustment,
        todayPrayerTimes,
        nextPrayer,
      });
    };
    loadData();
  }, []);

  const setCalcMethod = (method: CalcSettings['method']) => {
    localStorage.setItem(STORAGE_KEYS.CALC_METHOD, method);
    const newCalcSettings: CalcSettings = { ...data.calcSettings, method };
    localStorage.setItem(STORAGE_KEYS.CALC_SETTINGS, JSON.stringify(newCalcSettings));
    setData(prev => ({ ...prev, calcMethod: method, calcSettings: newCalcSettings }));
  };

  const setMadhab = (madhab: CalcSettings['madhab']) => {
    localStorage.setItem(STORAGE_KEYS.MADHAB, madhab);
    const newCalcSettings: CalcSettings = { ...data.calcSettings, madhab };
    localStorage.setItem(STORAGE_KEYS.CALC_SETTINGS, JSON.stringify(newCalcSettings));
    setData(prev => ({ ...prev, madhab, calcSettings: newCalcSettings }));
  };

  const setCalcSettings = (calcSettings: CalcSettings) => {
    localStorage.setItem(STORAGE_KEYS.CALC_SETTINGS, JSON.stringify(calcSettings));
    localStorage.setItem(STORAGE_KEYS.CALC_METHOD, calcSettings.method);
    localStorage.setItem(STORAGE_KEYS.MADHAB, calcSettings.madhab);
    setData(prev => ({ ...prev, calcSettings, calcMethod: calcSettings.method, madhab: calcSettings.madhab }));
  };

  const setLocation = (city: string, latitude: number, longitude: number) => {
    const location = { city, latitude, longitude };
    localStorage.setItem(STORAGE_KEYS.LOCATION, JSON.stringify(location));
    setData(prev => ({ ...prev, manualLocation: location }));
  };

  const setUseGPS = (useGPS: boolean) => {
    localStorage.setItem(STORAGE_KEYS.USE_GPS, String(useGPS));
    setData(prev => ({ ...prev, useGPS }));
  };

  const setAdhanEnabled = (enabled: boolean) => {
    localStorage.setItem(STORAGE_KEYS.ADHAN_ENABLED, String(enabled));
    setData(prev => ({ ...prev, adhanEnabled: enabled }));
  };

  const setAdhanSettings = (adhanSettings: AdhanSettings) => {
    localStorage.setItem(STORAGE_KEYS.ADHAN_SETTINGS, JSON.stringify(adhanSettings));
    setData(prev => ({ ...prev, adhanSettings }));
  };

  const setNotificationSettings = (notificationSettings: NotificationSettings) => {
    localStorage.setItem(STORAGE_KEYS.NOTIFICATION_SETTINGS, JSON.stringify(notificationSettings));
    setData(prev => ({ ...prev, notificationSettings }));
  };

  const setPersistentNotifications = (persistent: boolean) => {
    localStorage.setItem(STORAGE_KEYS.PERSISTENT_NOTIFICATIONS, String(persistent));
    setData(prev => ({ ...prev, persistentNotifications: persistent }));
  };

  const setIshaFixedSettings = (settings: IshaFixedSettings) => {
    localStorage.setItem(STORAGE_KEYS.ISHA_FIXED_SETTINGS, JSON.stringify(settings));
    setData(prev => ({ ...prev, ishaFixedSettings: settings }));
  };

  const setFajrFixedSettings = (settings: FajrFixedSettings) => {
    localStorage.setItem(STORAGE_KEYS.FAJR_FIXED_SETTINGS, JSON.stringify(settings));
    setData(prev => ({ ...prev, fajrFixedSettings: settings }));
  };

  const setHijriAdjustment = (adjustment: number) => {
    localStorage.setItem(STORAGE_KEYS.HIJRI_ADJUSTMENT, String(adjustment));
    setData(prev => ({ ...prev, hijriAdjustment: adjustment }));
  };

  const setTodayPrayerTimes = (times: TodayPrayerTimes) => {
    localStorage.setItem(STORAGE_KEYS.TODAY_PRAYER_TIMES, JSON.stringify(times));
    setData(prev => ({ ...prev, todayPrayerTimes: times }));
  };

  const setNextPrayer = (prayer: NextPrayerInfo) => {
    localStorage.setItem(STORAGE_KEYS.NEXT_PRAYER, JSON.stringify(prayer));
    setData(prev => ({ ...prev, nextPrayer: prayer }));
  };

  return {
    ...data,
    setCalcMethod,
    setMadhab,
    getCalcSettings: () => data.calcSettings,
    setCalcSettings,
    setLocation,
    setUseGPS,
    getAdhanEnabled: () => data.adhanEnabled,
    setAdhanEnabled,
    setAdhanSettings,
    setNotificationSettings,
    setPersistentNotifications,
    setIshaFixedSettings,
    setFajrFixedSettings,
    setHijriAdjustment,
    setTodayPrayerTimes,
    setNextPrayer,
  };
};
